import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { me, logout as apiLogout, type AuthOperator } from '../../api/auth';
import { ApiError } from '../../api/client';

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; operator: AuthOperator }
  | { status: 'unauthenticated' };

type AuthContextValue = AuthState & {
  refresh: () => Promise<void>;
  setOperator: (op: AuthOperator) => void;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const res = await me();
      setState({ status: 'authenticated', operator: res.operator });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'unauthenticated' });
        return;
      }
      console.error('[auth] me failed', err);
      setState({ status: 'unauthenticated' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setOperator = useCallback((operator: AuthOperator) => {
    setState({ status: 'authenticated', operator });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error('[auth] logout failed', err);
    }
    setState({ status: 'unauthenticated' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, setOperator, logout }),
    [state, refresh, setOperator, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
